import { useCallback } from 'react';
import { DateTime } from 'luxon';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import {
  GENERATION_TOOL,
  getMultiPromptSubtools,
  isDataDrivenGenerationTool,
} from 'src/data/generationTool';
import { addLanguageToGenerationResults } from 'src/data/variation';
import { useHistory } from 'src/lib/hooks/useHistory';
import { formatProjectRoute } from 'src/lib/routing';
import { ROUTE_PARAMS } from 'src/routes';
import { useNotifyAboutGenerationResult } from 'src/services';
import { useCurrentProjectId, useNotificationActions } from 'src/store';
import { useMultiPromptTemplateActions } from 'src/store/multiPromptTemplates';
import { usePageActions } from 'src/views/ProjectPage/store';

import {
  useDocumentViewActions,
  useGenerationActions,
  useListViewActions,
} from '../../../../store';
import { exportCsvHandler, getGenerationLoaderContent } from '../../../../utils';
import useEvents from '../../../CanvasProject/useEvents';
import { useLocalActions as useCanvasLocalActions } from '../../store';

import { MULTI_PROMPT_TEMPLATE_STEP } from './const';
import { useLocalActions } from './store';

export const useHandlers = ({ onClose }) => {
  const localActions = useLocalActions();
  const canvasLocalActions = useCanvasLocalActions();
  const listViewActions = useListViewActions();
  const documentViewActions = useDocumentViewActions();
  const generationActions = useGenerationActions();
  const projectPageActions = usePageActions();
  const multiPromptTemplateActions = useMultiPromptTemplateActions();
  const notificationActions = useNotificationActions();
  const notifyAboutGenerationResult = useNotifyAboutGenerationResult();
  const projectId = useCurrentProjectId();
  const events = useEvents();
  const confirmPopup = useConfirmPopup();
  const history = useHistory();

  const handleGenerationToolChange = useCallback(
    (generationTool) => {
      localActions.init(generationTool);
    },
    [localActions]
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleGenerate = useCallback(async () => {
    try {
      const generationToolFinal =
        localActions.getGenerationTool() || canvasLocalActions.getGenerationTool();
      let name;
      if (generationToolFinal === GENERATION_TOOL.customMultiPrompt) {
        const templateId = canvasLocalActions.getCustomMultiPromptId();
        name = multiPromptTemplateActions.getMultiPromptTemplateProperty(templateId, 'name');
      }
      const chainId = canvasLocalActions.getChainId();
      const csvChainId = canvasLocalActions.getCsvChainId();
      const loaderContent = getGenerationLoaderContent({
        generationTool: generationToolFinal,
        title: name,
        skipGetTextByTool: !!name,
      });
      if (csvChainId) {
        loaderContent.description += '.';
        loaderContent.tail = 'It may take a few minutes';
      }

      documentViewActions.setGeneratingLoaderText(loaderContent);
      canvasLocalActions.setGenerating(true);
      generationActions.setGenerating(true);
      projectPageActions.setGeneratingMultiPrompt(true);
      onClose();
      const language = canvasLocalActions.getLanguageForGeneration();
      const urlsForGeneration = canvasLocalActions.getUrlForGeneration();
      const briefExtractionPromise =
        canvasLocalActions.shouldWaitForBriefExtraction(urlsForGeneration);
      const { results, briefExtractionResult } = await localActions.generateVariations({
        projectId,
        language,
        chainIdForGeneration: chainId,
        csvChainIdForGeneration: csvChainId,
        options: {
          briefExtractionPromise,
        },
      });

      const resultsWithLanguage = addLanguageToGenerationResults(results);

      // store latest chainId
      const updatedChainId = resultsWithLanguage?.[0]?.chainId;
      if (updatedChainId) {
        canvasLocalActions.setChainId(updatedChainId);
      }

      const updatedCsvChainId = resultsWithLanguage?.[0]?.csvChainId;
      if (updatedCsvChainId) {
        canvasLocalActions.setCsvChainId(updatedCsvChainId);
      }
      events.trackSuggest({});

      if (resultsWithLanguage?.length > 0) {
        resultsWithLanguage.forEach((result) => {
          localActions.updateToolPromptIdByIndex(result.multiPromptStepIndex, result.blockId);
        });
        // Push received generation result to the "list view" store
        // Add generation result to the "list" view
        listViewActions.addGenerationResults(resultsWithLanguage);
        // Set request for "quality survey" and "list" scrolling
        const sortedResults = resultsWithLanguage.sort((a, b) => {
          const promptTools = getMultiPromptSubtools(generationToolFinal);
          return (
            Object.values(promptTools).indexOf(a.generationTool) -
            Object.values(promptTools).indexOf(b.generationTool)
          );
        });
        generationActions.setLastGenerationResult(sortedResults[0]);

        // Store "last generation timestamp" - required for "score" onboarding hint
        if (isDataDrivenGenerationTool(generationToolFinal)) {
          listViewActions.setLastGeneratedTimestamp(DateTime.local().toSeconds());
        }
        multiPromptTemplateActions.setMultiPromptBriefExtractionResultByChainId(
          updatedChainId,
          briefExtractionResult
        );
        if (csvChainId) {
          try {
            await confirmPopup({
              mode: CONFIRM_POPUP_MODE.confirmation,
              title: 'Your results are ready to download',
              content: `A total of ${
                [...new Set(results.map((res) => res.chainId))]?.length
              } results have been created.\nYou can download your results now or review and edit them in the list view before exporting.`,
              buttons: {
                confirm: 'Download results (CSV)',
                cancel: 'Review results',
              },
              withReject: true,
            });
            try {
              const csvMetaData = JSON.parse(results[0].csvMetaData);
              notificationActions.displayMessage('Exporting CSV...', null, { timeout: 1000 });
              await exportCsvHandler(csvMetaData, notificationActions);
            } catch {
              notificationActions.displayError({
                message: 'Failed to export CSV. Please try again later.',
              });
            }
          } catch {
            // if not on project page, go to it
            history.push(
              formatProjectRoute(projectId, {
                [ROUTE_PARAMS.generationTool]: GENERATION_TOOL.customMultiPrompt,
                [ROUTE_PARAMS.customMultiPromptId]: canvasLocalActions.getCustomMultiPromptId(),
              })
            );
          }
        }
      } else {
        notifyAboutGenerationResult({ result: { briefExtractionResult } });
      }
    } catch {
      notifyAboutGenerationResult({ result: {} });
    } finally {
      generationActions.setGenerating(false);
      canvasLocalActions.setGenerating(false);
      projectPageActions.setGeneratingMultiPrompt(false);
    }
  }, [
    localActions,
    listViewActions,
    generationActions,
    canvasLocalActions,
    documentViewActions,
    multiPromptTemplateActions,
    onClose,
    notifyAboutGenerationResult,
    events,
    projectId,
    confirmPopup,
    notificationActions,
    history,
    projectPageActions,
  ]);

  const handleStepSubmit = useCallback(async () => {
    const activeStep = canvasLocalActions.getMultiPromptActiveStep();
    switch (activeStep) {
      case MULTI_PROMPT_TEMPLATE_STEP.settings: {
        canvasLocalActions.incrementMultiPromptActiveStep();
        break;
      }

      case MULTI_PROMPT_TEMPLATE_STEP.review:
      default: {
        await handleGenerate();

        canvasLocalActions.resetMultiPromptActiveStep();
        break;
      }
    }
  }, [canvasLocalActions, handleGenerate]);

  const handleBack = useCallback(() => {
    canvasLocalActions.decrementMultiPromptActiveStep();
  }, [canvasLocalActions]);

  return {
    handleStepSubmit,
    handleCancel,
    handleBack,
    handleGenerationToolChange,
    handleGenerate,
  };
};
